import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/components/browse_photographers_page/PortfolioPreviewLightbox.css";
import { buildDisplayName } from "../../utils/nameFormatting";

const MEDIA_API_BASE = "https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev";
const PORTFOLIO_IMAGE_LIMIT = 12;

export default function PortfolioPreviewLightbox({ photographer, isOpen, onClose }) {
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !photographer?.sub) return;

    let cancelled = false;

    const loadPreview = async () => {
      setLoading(true);
      setError("");
      setCurrentIndex(0);

      try {
        const response = await fetch(
          `${MEDIA_API_BASE}/users/${photographer.sub}/portfolio-images?preview=true&limit=${PORTFOLIO_IMAGE_LIMIT}`
        );

        if (!response.ok) {
          throw new Error(`Failed to load portfolio preview (${response.status})`);
        }

        const data = await response.json();
        if (!cancelled) setPortfolioImages(Array.isArray(data) ? data : []);
      } catch (loadError) {
        console.error("Error loading portfolio preview:", loadError);
        if (!cancelled) setError("Could not load this portfolio preview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();
    return () => { cancelled = true; };
  }, [isOpen, photographer?.sub]);

  const previewImages = useMemo(() => {
    const images = [];
    const coverUrl = photographer?.cover_photo;

    if (coverUrl) {
      images.push({
        url: coverUrl,
        tags: { album_name: "Cover photo" },
        isCover: true,
      });
    }

    portfolioImages.forEach((image) => {
      if (!image?.url || image.url === coverUrl) return;
      images.push(image);
    });

    return images;
  }, [photographer?.cover_photo, portfolioImages]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key === "ArrowLeft" && previewImages.length > 1) {
        event.preventDefault();
        setCurrentIndex((index) => (index - 1 + previewImages.length) % previewImages.length);
      }
      if (event.key === "ArrowRight" && previewImages.length > 1) {
        event.preventDefault();
        setCurrentIndex((index) => (index + 1) % previewImages.length);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [isOpen, onClose, previewImages.length]);

  useEffect(() => {
    if (!isOpen || previewImages.length < 2) return;

    const indexes = [
      (currentIndex - 1 + previewImages.length) % previewImages.length,
      (currentIndex + 1) % previewImages.length,
    ];

    indexes.forEach((index) => {
      const image = new Image();
      image.src = previewImages[index].url;
    });
  }, [currentIndex, isOpen, previewImages]);

  if (!isOpen) return null;

  const currentImage = previewImages[currentIndex];
  const photographerName = buildDisplayName(
    photographer?.given_name,
    photographer?.family_name
  );
  const albumName = currentImage?.isCover
    ? "Cover photo"
    : currentImage?.tags?.album_name;

  const goPrevious = () => {
    setCurrentIndex((index) => (index - 1 + previewImages.length) % previewImages.length);
  };

  const goNext = () => {
    setCurrentIndex((index) => (index + 1) % previewImages.length);
  };

  return (
    <div
      ref={dialogRef}
      className="portfolio-preview"
      role="dialog"
      aria-modal="true"
      aria-label={`${photographerName || "Photographer"} portfolio preview`}
      tabIndex={-1}
      onClick={onClose}
    >
      <div className="portfolio-preview__topbar" onClick={(event) => event.stopPropagation()}>
        <div>
          <strong>{photographerName || "Portfolio preview"}</strong>
          {albumName && <span>{albumName}</span>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close portfolio preview">
          ×
        </button>
      </div>

      <div className="portfolio-preview__stage">
        {loading && !currentImage && (
          <div className="portfolio-preview__status">Loading portfolio…</div>
        )}

        {error && !currentImage && (
          <div className="portfolio-preview__status">{error}</div>
        )}

        {!loading && !error && !currentImage && (
          <div className="portfolio-preview__status">No portfolio images to preview yet.</div>
        )}

        {currentImage && (
          <img
            src={currentImage.url}
            alt={`${photographerName || "Photographer"} portfolio preview ${currentIndex + 1}`}
            onClick={(event) => event.stopPropagation()}
          />
        )}

        {previewImages.length > 1 && (
          <>
            <button
              type="button"
              className="portfolio-preview__nav portfolio-preview__nav--previous"
              onClick={(event) => {
                event.stopPropagation();
                goPrevious();
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              className="portfolio-preview__nav portfolio-preview__nav--next"
              onClick={(event) => {
                event.stopPropagation();
                goNext();
              }}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}
      </div>

      <div className="portfolio-preview__footer" onClick={(event) => event.stopPropagation()}>
        <span>
          {currentImage ? `${currentIndex + 1} / ${previewImages.length}` : ""}
        </span>
        {photographer?.url_slug && (
          <a href={`/profile/${photographer.url_slug}`}>View full profile</a>
        )}
      </div>
    </div>
  );
}
